/**
 * Motion.
 *
 * Everything that moves on a schedule rather than in response to a single
 * control lives here: the scroll reveals, the header that steps out of the
 * way, the one-time nudge that tells a thumb a row scrolls sideways.
 *
 * Three rules hold throughout:
 *
 *   1. Nothing here is load-bearing. If this file never runs, the page is
 *      complete and readable — critical.css only hides a revealing element
 *      while `html.js` is set, and that class is written in <head>.
 *   2. prefers-reduced-motion is checked once, up front. When it is set,
 *      this file reveals everything immediately and then does nothing else.
 *      Not a gentler site — a still one.
 *   3. The observers unobserve as soon as they have fired. A phone should
 *      not carry fifty live IntersectionObservers down a long page.
 */
(function () {
  const root = document.documentElement;
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** Put every revealing element in its final state and leave it there. */
  function revealEverything() {
    document.querySelectorAll('[data-reveal]').forEach((el) => {
      el.style.removeProperty('--reveal-delay');
      el.classList.add('is-revealed');
    });
  }

  /* ---- Scroll reveal ---- */
  function startReveals() {
    const targets = document.querySelectorAll('[data-reveal]');
    if (!targets.length) return;

    /*
     * Elements sharing a [data-reveal-group] come in one after another. The
     * delay is assigned on entry rather than at load, so a row that is
     * scrolled past quickly does not inherit a stale stagger.
     */
    const observer = new IntersectionObserver(
      (entries, obs) => {
        const arriving = entries.filter((e) => e.isIntersecting);

        arriving.forEach((entry) => {
          const el = entry.target;
          const group = el.closest('[data-reveal-group]');

          if (group) {
            const siblings = [...group.querySelectorAll('[data-reveal]')];
            const index = siblings.indexOf(el);
            if (index > 0) {
              el.style.setProperty('--reveal-delay', `${index * 70}ms`);
            }
          }

          el.classList.add('is-revealed');
          obs.unobserve(el);
        });
      },
      {
        /*
         * Fire a little before the element is actually on screen, so the
         * movement is finishing as it arrives rather than starting once it
         * is already being read. The negative bottom margin means an
         * element must be properly in view, not just peeking.
         */
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.01,
      }
    );

    targets.forEach((el) => observer.observe(el));
  }

  /* ---- Header steps out of the way ---- */
  function startHeader() {
    const header = document.querySelector('.shop-header');
    if (!header) return;

    let last = window.scrollY;
    let ticking = false;

    function update() {
      const y = Math.max(0, window.scrollY);
      const menuOpen = document.querySelector('.shop-menu:not([hidden])');

      /*
       * Never hide the bar while the menu is open, while something inside it
       * has focus, or near the top of the page. A header that vanishes under
       * a keyboard user's cursor is a trap, not a flourish.
       */
      const holdOpen = menuOpen || header.contains(document.activeElement) || y < 120;

      if (holdOpen) {
        header.classList.remove('is-tucked');
      } else if (y > last + 6) {
        header.classList.add('is-tucked');
      } else if (y < last - 6) {
        header.classList.remove('is-tucked');
      }

      last = y;
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );

    /* Focus entering the header always brings it back. */
    header.addEventListener('focusin', () => header.classList.remove('is-tucked'));
  }

  /* ---- Tell a thumb the row scrolls ---- */
  function startRailHints() {
    const rails = document.querySelectorAll('.rail');
    if (!rails.length) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const rail = entry.target;
          obs.unobserve(rail);

          /* Nothing to hint at if it all fits already. */
          if (rail.scrollWidth <= rail.clientWidth + 8) return;

          window.setTimeout(() => {
            rail.scrollTo({ left: 34, behavior: 'smooth' });
            window.setTimeout(() => rail.scrollTo({ left: 0, behavior: 'smooth' }), 520);
          }, 420);
        });
      },
      { threshold: 0.4 }
    );

    rails.forEach((rail) => observer.observe(rail));
  }

  /* ---- The bag badge answers when it changes ---- */
  function startCartPulse() {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      let previous = badge.textContent.trim();

      new MutationObserver(() => {
        const now = badge.textContent.trim();
        if (now === previous) return;
        previous = now;

        badge.classList.remove('is-bumped');
        /* Reflow, so the same animation can run twice in a row. */
        void badge.offsetWidth;
        badge.classList.add('is-bumped');
      }).observe(badge, { childList: true, characterData: true, subtree: true });
    });
  }

  function start() {
    if (still.matches) {
      revealEverything();
      return;
    }

    startReveals();
    startHeader();
    startRailHints();
    startCartPulse();
  }

  /*
   * A visitor can turn reduced motion on while the page is open. Honour it
   * immediately rather than waiting for a reload.
   */
  still.addEventListener('change', () => {
    if (still.matches) revealEverything();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
